import { createSelector } from 'reselect';
import parseServerValidationError from '../helpers/parseServerValidationError';

import {
  validateApplicantAddressOMK,
  validateApplicantAddressAddress,
  validateApplicantAddressArea,
  validateApplicantAddressDistrict,
  validateApplicantAddressHouseAndBuilding,
  validateApplicantAddressStreet,
  validateApplicantChangedName,
  validateApplicantChangeName,
  validateApplicantChangeNameDocIssuer,
  validateApplicantCitizenship,
  validateApplicantDateOfMarriage,
  validateApplicantBirthPlace,
  validateApplicantDocumentDivisionCode,
  validateApplicantDocumentIssuedDate,
  validateApplicantDocumentIssuer,
  validateApplicantDocumentNumber,
  validateApplicantDocumentType,
  validateApplicantLivingTogether,
  validateApplicantMaritalStatus,
  validateApplicantMarriageRecordHolder,
  validateApplicantMarriageRecordNumber,
  validateApplicantPhone,
  validateApplicantAddressFlat,
  validateApplicantSex,
  validateApplicantChangedSurname,
  validateApplicantEmail,
  validateApplicantChangedPatronimic,
} from './applicant';

import { validateChildrenCards, validateChildrenQuantity } from './children';

import {
  validateChildAddressOMK,
  validateChildAddressAddress,
  validateChildAddressArea,
  validateChildAddressCheckbox,
  validateChildAddressDistrict,
  validateChildAddressHouseAndBuilding,
  validateChildAddressStreet,
  validateChildBirthCertificateIssueDate,
  validateChildBirthCertificateIssuer,
  validateChildBirthCertificateNumber,
  validateChildBirthCertificateType,
  validateChildBirthDate,
  validateChildBirthStatus,
  validateChildChangedName,
  validateChildChangedNameIssuer,
  validateChildChangeNameCheckbox,
  validateChildCitizenship,
  validateChildBirthPlace,
  validateChildDocumentDivisionCode,
  validateChildDocumentIssuedDate,
  validateChildDocumentIssuer,
  validateChildDocumentNumber,
  validateChildDocumentType,
  validateChildHomestay,
  validateChildName,
  validateChildSecondParent,
  validateChildSex,
  validateChildSurname,
  validatePaternityRegisterDate,
  validatePaternityRegisterNumber,
  validateSecondParentBirthdate,
  validateSecondParentDeathCertIssuer,
  validateSecondParentDeathdate,
  validateSecondParentDivorceCertIssuer,
  validateSecondParentDivorceDate,
  validateSecondParentMarriageDate,
  validateSecondParentName,
  validateSecondParentSurname,
  validateWidowhood,
  validateChildEducation,
  validateChildChangedSurname,
  validateChildChangedPatronimic,
  validateChildClinic,
  validateChildAddressFlat,
  validateBornNotInCurrentMarriage,
  validateChildPatronymic,
  validateSecondParentPatronymic,
} from './child';
import {
  validateSpouseAddressOMK,
  validateSpouseAddressAddress,
  validateSpouseAddressArea,
  validateSpouseAddressDistrict,
  validateSpouseAddressHouseAndBuilding,
  validateSpouseAddressMatchWithApplicant,
  validateSpouseAddressStreet,
  validateSpouseBirthDate,
  validateSpouseChangedName,
  validateSpouseChangeName,
  validateSpouseChangeNameDocIssuer,
  validateSpouseCitizenship,
  validateSpouseBirthPlace,
  validateSpouseDocumentDivisionCode,
  validateSpouseDocumentIssuedDate,
  validateSpouseDocumentIssuer,
  validateSpouseDocumentNumber,
  validateSpouseDocumentType,
  validateSpouseName,
  validateSpousePatronimic,
  validateSpouseSurname,
  validateSpouseAddressFlat,
  validateSpouseChangedSurname,
  validateSpouseChangedPatronimic,
} from './spouse';
import {
  validateConsentWithTerms,
  validateWarnedAboutResponsibility,
} from './agreements';
import { validateApplicantPassportStatus } from '../selectors/applicant';
import { validateChildPassportStatus } from '../selectors/child';
import { validateSpousePassportStatus } from '../selectors/spouse';
import { validateChildrenPassportStatus } from '../selectors/children';

const childAddressValidations = {
  'child.address.omk': validateChildAddressOMK,
  'child.address.area': validateChildAddressArea,
  'child.address.district': validateChildAddressDistrict,
  'child.address.house': validateChildAddressHouseAndBuilding,
  'child.address.street': validateChildAddressStreet,
  'child.address.address': validateChildAddressAddress,
  'child.address.flat': validateChildAddressFlat,
};

const singleChildValidation = {
  'child.name': validateChildName,
  'child.surname': validateChildSurname,
  'child.patronymic': validateChildPatronymic,
  'child.sex': validateChildSex,
  'child.birthdate': validateChildBirthDate,
  'child.citizenship': validateChildCitizenship,
  'child.birthCertificate.type': validateChildBirthCertificateType,
  'child.birthCertificate.number': validateChildBirthCertificateNumber,
  'child.birthCertificate.issueDate': validateChildBirthCertificateIssueDate,
  'child.birthCertificate.issuer': validateChildBirthCertificateIssuer,
  'child.dul.type': validateChildDocumentType,
  'child.dul.number': validateChildDocumentNumber,
  'child.dul.issuer': validateChildDocumentIssuer,
  'child.dul.issueDate': validateChildDocumentIssuedDate,
  'child.dul.divisionCode': validateChildDocumentDivisionCode,
  'child.birthPlace': validateChildBirthPlace,
  'child.education.institutionName': validateChildEducation,
  'child.polyclinic.institutionName': validateChildClinic,
  'child.addressMatchWithApplicant': validateChildAddressCheckbox,
  'child.confirmingDocs': validateChildHomestay,
  'child.changeName': validateChildChangeNameCheckbox,
  'child.changedName': validateChildChangedName,
  'child.changedSurname': validateChildChangedSurname,
  'child.changedPatronimic': validateChildChangedPatronimic,
  'child.changeNameDocIssuer': validateChildChangedNameIssuer,
  'child.bornInCurrentMarriage': validateChildBirthStatus,
  'child.bornInNotCurrentMarriage': validateBornNotInCurrentMarriage,
  'child.secondParent.status': validateChildSecondParent,
  'child.secondParent.paternityRegisterDate': validatePaternityRegisterDate,
  'child.secondParent.paternityRegisterNumber': validatePaternityRegisterNumber,
  'child.secondParent.proofOfDeath': validateWidowhood,
  'child.secondParent.name': validateSecondParentName,
  'child.secondParent.patronymic': validateSecondParentPatronymic,
  'child.secondParent.surname': validateSecondParentSurname,
  'child.secondParent.deathDocIssuer': validateSecondParentDeathCertIssuer,
  'child.secondParent.birthdate': validateSecondParentBirthdate,
  'child.secondParent.deathdate': validateSecondParentDeathdate,
  'child.secondParent.dateOfMarriage': validateSecondParentMarriageDate,
  'child.secondParent.dateOfDivorce': validateSecondParentDivorceDate,
  'child.secondParent.divorceDocIssuer': validateSecondParentDivorceCertIssuer,
  ...childAddressValidations,
};

const spouseAddressValidations = {
  'spouse.address.omk': validateSpouseAddressOMK,
  'spouse.address.area': validateSpouseAddressArea,
  'spouse.address.district': validateSpouseAddressDistrict,
  'spouse.address.house': validateSpouseAddressHouseAndBuilding,
  'spouse.address.street': validateSpouseAddressStreet,
  'spouse.address.address': validateSpouseAddressAddress,
  'spouse.address.flat': validateSpouseAddressFlat,
};

const spouseValidations = {
  'spouse.name': validateSpouseName,
  'spouse.surname': validateSpouseSurname,
  'spouse.patronymic': validateSpousePatronimic,
  'spouse.birthdate': validateSpouseBirthDate,
  'spouse.citizenship': validateSpouseCitizenship,
  'spouse.addressMatchWithApplicant': validateSpouseAddressMatchWithApplicant,
  'spouse.dul.type': validateSpouseDocumentType,
  'spouse.dul.number': validateSpouseDocumentNumber,
  'spouse.dul.issuer': validateSpouseDocumentIssuer,
  'spouse.dul.issueDate': validateSpouseDocumentIssuedDate,
  'spouse.dul.divisionCode': validateSpouseDocumentDivisionCode,
  'spouse.birthPlace': validateSpouseBirthPlace,
  'spouse.changeName': validateSpouseChangeName,
  'spouse.changedName': validateSpouseChangedName,
  'spouse.changedSurname': validateSpouseChangedSurname,
  'spouse.changedPatronimic': validateSpouseChangedPatronimic,
  'spouse.changeNameDocIssuer': validateSpouseChangeNameDocIssuer,
  ...spouseAddressValidations,
};

const applicantAddressValidations = {
  'applicant.address.omk': validateApplicantAddressOMK,
  'applicant.address.area': validateApplicantAddressArea,
  'applicant.address.district': validateApplicantAddressDistrict,
  'applicant.address.house': validateApplicantAddressHouseAndBuilding,
  'applicant.address.street': validateApplicantAddressStreet,
  'applicant.address.address': validateApplicantAddressAddress,
  'applicant.address.flat': validateApplicantAddressFlat,
};
const applicantValidations = {
  'applicant.phone': validateApplicantPhone,
  'applicant.email': validateApplicantEmail,
  'applicant.sex': validateApplicantSex,
  'applicant.citizenship': validateApplicantCitizenship,
  'applicant.dul.type': validateApplicantDocumentType,
  'applicant.dul.number': validateApplicantDocumentNumber,
  'applicant.dul.issuer': validateApplicantDocumentIssuer,
  'applicant.dul.issueDate': validateApplicantDocumentIssuedDate,
  'applicant.dul.divisionCode': validateApplicantDocumentDivisionCode,
  'applicant.birthPlace': validateApplicantBirthPlace,
  'applicant.married': validateApplicantMaritalStatus,
  'applicant.dateOfMarriage': validateApplicantDateOfMarriage,
  'applicant.marriageRecordNumber': validateApplicantMarriageRecordNumber,
  'applicant.marriageRecordHolder': validateApplicantMarriageRecordHolder,
  'applicant.livingTogether': validateApplicantLivingTogether,
  'applicant.changeName': validateApplicantChangeName,
  'applicant.changedName': validateApplicantChangedName,
  'applicant.changedSurname': validateApplicantChangedSurname,
  'applicant.changedPatronimic': validateApplicantChangedPatronimic,
  'applicant.changeNameDocIssuer': validateApplicantChangeNameDocIssuer,
  ...applicantAddressValidations,
};

const childrenValidations = {
  'children.quantity': validateChildrenQuantity,
  'cards.hasErrors': validateChildrenCards,
};

const agreementsValidations = {
  'agreements.warnedAboutResponsibility': validateWarnedAboutResponsibility,
  'agreements.consentWithTerms': validateConsentWithTerms,
};

export const passportIssueDateValidation = {
  applicant: validateApplicantPassportStatus,
  spouse: validateSpousePassportStatus,
  child: validateChildPassportStatus,
  children: validateChildrenPassportStatus,
};

const formValidations = {
  ...applicantValidations,
  ...spouseValidations,
  ...childrenValidations,
  ...agreementsValidations,
};

const stepCompleted = (...validations) => {
  return validations.every((hasErrors) => !hasErrors);
};

const validateForm = createSelector(
  Object.values(formValidations),
  (...validations) => {
    return Object.keys(formValidations).reduce((acc, name, i) => {
      return {
        ...acc,
        [name]: validations[i],
      };
    }, {});
  }
);

const validateChild = createSelector(
  Object.values(singleChildValidation),
  (...validations) => {
    return Object.keys(singleChildValidation).reduce((acc, name, i) => {
      return {
        ...acc,
        [name]: validations[i],
      };
    }, {});
  }
);
export const validators = {
  ...formValidations,
  ...singleChildValidation,
  childrenStepCompleted: createSelector(
    validateChildrenQuantity,
    validateChildrenCards,
    stepCompleted
  ),
  applicantStepCompleted: createSelector(
    Object.values(applicantValidations),
    stepCompleted
  ),
  spouseStepCompleted: createSelector(
    Object.values(spouseValidations),
    stepCompleted
  ),
  validateForm,
  validateChild,
};

export const validateField: (
  type,
  state,
  documentField?: string | null
) => Record<string, { error: boolean | string }> = (
  type,
  state,
  documentField = null
) => {
  const [modelNamespace, field] = type.split('/');

  const action = documentField
    ? `${modelNamespace}.${documentField}.type`
    : `${modelNamespace}.${field}`;

  const error = (validators[action] && validators[action](state)) || false;

  const fieldValidationResult = parseServerValidationError({ [action]: error });
  return fieldValidationResult[modelNamespace];
};

export const hasErrors = (object) => {
  return Object.values(object).some(
    (
      values:
        | { error: boolean | string }
        | { [x: string]: { error: boolean | string } }
    ) => {
      if (values.hasOwnProperty('error')) return values.error;
      for (const key in values) {
        if (values.hasOwnProperty(key)) {
          return hasErrors(values);
        }
      }
      return null;
    }
  );
};
